<script setup lang="ts">
import type { UploadId, UploadingFile } from '#layers/assets/stores/upload';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';
import { useUploadStore } from '#layers/assets/stores/upload';
import { formatFilesize } from '#layers/assets/utils/format-filesize';

const appConfig = useAppConfig();
const uploadStore = useUploadStore();
const { t, te } = useI18n();

const isPanelExpanded = ref(false);
const isButtonExpanded = useButtonExpanded();

const hasActiveUploads = computed(() => uploadStore.allFiles.length > 0);

const uploadingCount = computed(() => uploadStore.uploadingFiles.length);

const statusIcon = computed(() => {
	if (uploadingCount.value > 0) return appConfig.ui.icons.loader;
	if (uploadStore.failedFiles.length > 0) return 'lucide:circle-x';
	return 'lucide:circle-check';
});

const statusIconClasses = computed(() => {
	if (uploadingCount.value > 0) return 'animate-spin';
	if (uploadStore.failedFiles.length > 0) return 'text-error';
	return 'text-success';
});

const statusSummary = computed(() => {
	const completedCount = uploadStore.completedFiles.length;
	const failedCount = uploadStore.failedFiles.length;

	if (uploadingCount.value > 0) return t('upload.n_uploading', { n: uploadingCount.value });
	if (failedCount > 0) return t('upload.n_failed', { n: failedCount });
	if (completedCount > 0) return t('upload.n_completed', { n: completedCount });

	return '';
});

function getFileStatusIcon(status: UploadingFile['status']) {
	switch (status) {
		case 'success':
			return 'lucide:circle-check';
		case 'aborted':
		case 'error':
			return 'lucide:circle-x';
		case 'uploading':
			return 'lucide:loader-circle';
		default:
			return '';
	}
}

function getFileStatusIconClass(status: UploadingFile['status']) {
	switch (status) {
		case 'uploading':
			return 'animate-spin';
		default:
			return '';
	}
}

function getFileStatusColorClass(status: UploadingFile['status']) {
	switch (status) {
		case 'success':
			return 'text-success';
		case 'error':
			return 'text-error';
		default:
			return 'text-muted';
	}
}

function getFileStatusText(status: UploadingFile['status']) {
	const key = `upload.status.${status}`;
	return te(key) ? t(key) : undefined;
}

// A failed upload larger than the server limit was rejected for its size.
function getErrorMessages(upload: UploadingFile) {
	const { maxFileUploadSizeBytes, resolveUploadErrorMessage } = useFileUploadLimit();

	const tooLarge = maxFileUploadSizeBytes !== null && upload.file.size > maxFileUploadSizeBytes;

	return {
		label: tooLarge ? t('upload.too_large') : t('upload.status.error'),
		detailed: resolveUploadErrorMessage(upload.error),
	};
}

function canRemoveFile(status: UploadingFile['status']) {
	return status === 'error' || status === 'success' || status === 'aborted';
}

function canAbortUpload(status: UploadingFile['status']) {
	return status === 'pending' || status === 'uploading';
}

function clearCompleted() {
	uploadStore.clearCompleted();

	if (!hasActiveUploads.value) {
		isPanelExpanded.value = false;
	}
}

function removeFile(id: UploadId) {
	uploadStore.removeFile(id);
}

function abortUpload(id: UploadId) {
	uploadStore.abortUpload(id);
}

function useButtonExpanded() {
	const forceButtonExpanded = ref(false);

	let buttonExpandedTimeout: NodeJS.Timeout | null = null;

	// expand the button if the number if uploads in the upload store changed and contract it after 5 seconds
	watch(() => uploadStore.allFiles, () => {
		forceButtonExpanded.value = true;

		if (buttonExpandedTimeout) {
			clearTimeout(buttonExpandedTimeout);
		}

		buttonExpandedTimeout = setTimeout(() => {
			forceButtonExpanded.value = false;
		}, 5000);
	});

	return computed(() => uploadingCount.value > 0 || forceButtonExpanded.value);
}
</script>

<template>
	<Transition
		enter-active-class="transition-all duration-200"
		enter-from-class="translate-y-full opacity-0"
		enter-to-class="translate-y-0 opacity-100"
		leave-active-class="transition-all duration-200"
		leave-from-class="translate-y-0 opacity-100"
		leave-to-class="translate-y-full opacity-0"
	>
		<div
			v-if="hasActiveUploads"
			class="fixed bottom-4 left-16 z-50 overflow-hidden border bg-default shadow-lg transition-all duration-100"
			:class="{
				'rounded-3xl border-accented': !isPanelExpanded,
				'rounded-lg': isPanelExpanded,
			}"
		>
			<DAnimatedSheet
				:duration="500"
			>
				<DButton
					v-if="!isPanelExpanded"
					variant="ghost"
					color="neutral"
					class="group h-9 rounded-full px-2 text-xs hover:pe-4.5"
					:icon="statusIcon"
					:ui="{
						leadingIcon: statusIconClasses,
					}"
					:class="{
						'pe-4.5': isButtonExpanded,
					}"
					@click="isPanelExpanded = true"
				>
					<span class="group-hover:inline" :class="{ hidden: !isButtonExpanded }">
						{{ statusSummary }}
					</span>
				</DButton>

				<template v-else>
					<div
						class="w-80 overflow-hidden"
					>
						<div
							class="flex items-center justify-between border-b px-4 py-3"
						>
							<h3 class="text-sm font-semibold">
								{{ t('upload.progress.title') }}
							</h3>
							<DButton
								variant="ghost"
								color="neutral"
								icon="lucide:minus"
								size="xs"
								@click="isPanelExpanded = false"
							/>
						</div>

						<DList
							:items="uploadStore.allFiles"
							class="max-h-80 overflow-y-auto border-none"
							:ui="{ item:
								{
									base: 'px-4',
									textWrapper: 'gap-0.5',
									label: 'text-xs',
									description: 'text-xs text-dimmed',
									trailing: `ms-4`,
								},
							}"
						>
							<template #item-leading="{ item: upload }">
								<FileTypeIcon
									class="h-8 shrink-0"
									:file="upload.file"
								/>
							</template>

							<template #item-label="{ item: upload }">
								{{ upload.file.name }}
							</template>

							<template #item-description="{ item: upload }">
								<span class="inline-flex items-center gap-2">
									<span>{{ formatFilesize(upload.file.size) }}</span>
									<DSeparator orientation="vertical" class="h-3" />
									<span class="inline-flex items-center gap-1" :class="getFileStatusColorClass(upload.status)">
										<DIcon
											class="shrink-0"
											size="12"
											:name="getFileStatusIcon(upload.status)"
											:class="getFileStatusIconClass(upload.status)"
										/>
										<DTooltip v-if="upload.status === 'error' && upload.error" :text="getErrorMessages(upload).detailed">
											<span class="font-medium">{{ getErrorMessages(upload).label }}</span>
										</DTooltip>
										<span v-else-if="getFileStatusText(upload.status)" class="font-medium">
											{{ getFileStatusText(upload.status) }}
										</span>
									</span>
								</span>
							</template>

							<template #item-trailing="{ item: file }">
								<DTooltip
									v-if="canRemoveFile(file.status)"
									:text="$t('common.clear')"
								>
									<DButton
										variant="ghost"
										color="neutral"
										icon="lucide:x"
										size="xs"
										@click="removeFile(file.id)"
									/>
								</DTooltip>
								<DTooltip
									v-if="canAbortUpload(file.status)"
									:text="$t('common.abort')"
								>
									<DButton
										variant="ghost"
										color="neutral"
										icon="lucide:x"
										size="xs"
										@click="abortUpload(file.id)"
									/>
								</DTooltip>
							</template>
						</DList>

						<div
							v-if="uploadStore.completedFiles.length > 0"
							class="flex items-center justify-end border-t px-4 py-1.5"
						>
							<DButton
								variant="ghost"
								color="neutral"
								:label="t('common.clear')"
								size="sm"
								@click="clearCompleted"
							/>
						</div>
					</div>
				</template>
			</DAnimatedSheet>
		</div>
	</Transition>
</template>
